import { useEffect, useRef, useState } from "react";
import { situationCardSlotOrder, type SituationCardDetail } from "@peer-start/platform-contracts";
import {
  situationCardCopy,
  type SituationCardCopy,
  type SituationCardSection,
} from "../pages/situation-card-view.js";

const sections: readonly SituationCardSection[] = situationCardSlotOrder;

export type CardPlayback =
  | { phase: "loading" }
  | { phase: "streaming" | "settled"; paragraphs: SituationCardCopy; changed: SituationCardSection[] };

export function useCardPlayback() {
  const [playback, setPlayback] = useState<CardPlayback | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function start(before: SituationCardCopy, request: Promise<SituationCardDetail>) {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setPlayback({ phase: "loading" });
    const started = Date.now();
    try {
      const detail = await request;
      const next = situationCardCopy(detail.title, detail.summary, detail.slots);
      const changed = sections.filter((key) => before[key] !== next[key]);
      const wait = Math.max(0, 800 - (Date.now() - started));
      timer.current = window.setTimeout(() => {
        setPlayback({ phase: "streaming", paragraphs: next, changed });
      }, wait);
    } catch {
      setPlayback(null);
    }
  }

  function settle() {
    setPlayback((current) =>
      current?.phase === "streaming" ? { phase: "settled", paragraphs: current.paragraphs, changed: current.changed } : current,
    );
    timer.current = window.setTimeout(() => setPlayback(null), 1400);
  }

  return { playback, start, settle };
}
